import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Plus, PencilSimple, Trash, Tag, Megaphone, Sparkle, Eye, EyeSlash, X, Check, ChartLine } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart } from "recharts";
import { CHART_TOOLTIP_STYLE, CHART_TICK_XS } from "../config/uiHelpers";

const CONFIGS = {
  offers: {
    label: "Offers",
    icon: Tag,
    endpoint: "/admin/offers",
    idField: "offer_id",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "code", label: "Promo Code", type: "text", required: true, placeholder: "e.g. DIWALI25" },
      { key: "discount", label: "Discount Label", type: "text", required: true, placeholder: "e.g. 25% OFF" },
      { key: "category", label: "Category", type: "text", placeholder: "general / cctv / alarm" },
      { key: "image_url", label: "Image URL", type: "text" },
      { key: "valid_until", label: "Valid Until (ISO)", type: "text", placeholder: "2026-12-31T23:59:59+00:00" },
      { key: "bg_color", label: "Background Color", type: "color" },
      { key: "active", label: "Active", type: "checkbox" },
    ],
    defaults: { title: "", description: "", code: "", discount: "", category: "general", image_url: "", valid_until: "", bg_color: "#002FA7", active: true },
    preview: (item) => (
      <div className="text-xs text-slate-600">Code: <span className="font-mono font-bold">{item.code}</span> · {item.discount}</div>
    )
  },
  announcements: {
    label: "Announcements",
    icon: Megaphone,
    endpoint: "/admin/announcements",
    idField: "announcement_id",
    fields: [
      { key: "emoji", label: "Emoji", type: "text", placeholder: "🎉" },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "message", label: "Message", type: "textarea", required: true },
      { key: "type", label: "Type", type: "select", options: ["festival", "service", "support", "promo"] },
      { key: "link", label: "Link", type: "text", placeholder: "/products" },
      { key: "priority", label: "Priority (higher = sooner)", type: "number" },
      { key: "accent", label: "Accent Color", type: "color" },
      { key: "active", label: "Active", type: "checkbox" },
    ],
    defaults: { emoji: "🎉", title: "", message: "", type: "festival", link: "#", priority: 5, accent: "#002FA7", active: true },
    preview: (item) => (
      <div className="text-xs text-slate-600">{item.emoji} · Priority {item.priority} · {item.type}</div>
    )
  },
  campaigns: {
    label: "Promo Campaigns",
    icon: Sparkle,
    endpoint: "/admin/promo-campaigns",
    idField: "campaign_id",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "tagline", label: "Tagline", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "image_url", label: "Image URL", type: "text" },
      { key: "cta_label", label: "CTA Label", type: "text" },
      { key: "cta_link", label: "CTA Link", type: "text" },
      { key: "badge", label: "Badge", type: "text", placeholder: "Most Popular" },
      { key: "accent", label: "Accent Color", type: "color" },
      { key: "active", label: "Active", type: "checkbox" },
    ],
    defaults: { title: "", tagline: "", description: "", image_url: "", cta_label: "Learn More", cta_link: "/products", badge: "Featured", accent: "#002FA7", active: true },
    preview: (item) => (
      <div className="text-xs text-slate-600">{item.badge} · {item.tagline}</div>
    )
  }
};

const PromotionsManager = ({ user, logout }) => {
  const [activeTab, setActiveTab] = useState("offers");
  const [items, setItems] = useState({ offers: [], announcements: [], campaigns: [] });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit', item: {...}, type }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, a, c, stats] = await Promise.all([
        axios.get(`${API}/admin/offers`),
        axios.get(`${API}/admin/announcements`),
        axios.get(`${API}/admin/promo-campaigns`),
        axios.get(`${API}/admin/redemption-stats`).catch(() => ({ data: null })),
      ]);
      setItems({ offers: o.data, announcements: a.data, campaigns: c.data });
      setAnalytics(stats.data);
    } catch (err) {
      toast.error("Failed to load promotions");
    } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isAnalytics = activeTab === "analytics";
  const config = CONFIGS[activeTab];
  const Icon = isAnalytics ? ChartLine : config.icon;
  const list = isAnalytics ? [] : items[activeTab];

  const openCreate = () => setEditing({ mode: "create", item: { ...config.defaults }, type: activeTab });
  const openEdit = (item) => setEditing({ mode: "edit", item: { ...item }, type: activeTab });
  const closeEditor = () => setEditing(null);

  const save = async () => {
    const cfg = CONFIGS[editing.type];
    try {
      const payload = { ...editing.item };
      // Strip readonly fields
      delete payload._id;
      delete payload[cfg.idField];
      delete payload.created_at;
      // Coerce numeric
      cfg.fields.forEach(f => {
        if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
          payload[f.key] = Number(payload[f.key]);
        }
      });
      if (editing.mode === "create") {
        await axios.post(`${API}${cfg.endpoint}?requester_role=${user?.role || ""}`, payload);
        toast.success(`${cfg.label.slice(0, -1)} created`);
      } else {
        const id = editing.item[cfg.idField];
        await axios.put(`${API}${cfg.endpoint}/${id}?requester_role=${user?.role || ""}`, payload);
        toast.success(`${cfg.label.slice(0, -1)} updated`);
      }
      closeEditor();
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}${config.endpoint}/${item[config.idField]}?requester_role=${user?.role || ""}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) { toast.error("Delete failed"); }
  };

  const toggleActive = async (item) => {
    try {
      const payload = { ...item, active: !item.active };
      delete payload._id;
      delete payload[config.idField];
      delete payload.created_at;
      await axios.put(`${API}${config.endpoint}/${item[config.idField]}?requester_role=${user?.role || ""}`, payload);
      fetchAll();
    } catch (err) { toast.error("Toggle failed"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8 max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                Promotions
              </h1>
              <p className="text-base text-slate-700 mt-1">Launch festival offers, announcements &amp; campaigns — no code required.</p>
            </div>
            <button
              data-testid="promo-create-btn"
              onClick={openCreate}
              disabled={isAnalytics}
              className={`rounded-none px-6 py-3 font-medium flex items-center gap-2 transition-colors duration-300 ${isAnalytics ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-[#002FA7] text-white hover:bg-[#00227A]'}`}
            >
              <Plus size={18} weight="bold" /> New {isAnalytics ? 'Item' : config.label.slice(0, -1)}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-slate-200 mt-6 overflow-x-auto">
            {Object.entries(CONFIGS).map(([key, cfg]) => {
              const TIcon = cfg.icon;
              const count = items[key].length;
              return (
                <button
                  key={key}
                  data-testid={`promo-tab-${key}`}
                  onClick={() => setActiveTab(key)}
                  className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${activeTab === key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  <TIcon size={16} weight={activeTab === key ? "fill" : "regular"} />
                  {cfg.label} <span className="text-xs text-slate-400">({count})</span>
                </button>
              );
            })}
            <button
              key="analytics"
              data-testid="promo-tab-analytics"
              onClick={() => setActiveTab("analytics")}
              className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${activeTab === 'analytics' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            >
              <ChartLine size={16} weight={activeTab === 'analytics' ? 'fill' : 'regular'} />
              Analytics <span className="text-xs text-slate-400">({analytics?.total_redemptions || 0})</span>
            </button>
          </div>

          {/* List / Analytics */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : isAnalytics ? (
            <AnalyticsPanel analytics={analytics} />
          ) : list.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-none p-16 text-center shadow-sm">
              <Icon size={48} weight="duotone" className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No {config.label.toLowerCase()} yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-none shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Title</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Details</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Status</th>
                    <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(item => (
                    <tr key={item[config.idField]} data-testid={`promo-row-${item[config.idField]}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-300">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-1 h-10" style={{ backgroundColor: item.bg_color || item.accent || '#002FA7' }}></div>
                          <div>
                            <div className="font-semibold text-sm">{item.title}</div>
                            <div className="text-xs text-slate-400 font-mono mt-0.5">{item[config.idField]}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">{config.preview(item)}</td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => toggleActive(item)}
                          data-testid={`promo-toggle-${item[config.idField]}`}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-none border text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${item.active ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-100 border-slate-200 text-slate-500'}`}
                        >
                          {item.active ? <Eye size={12} weight="fill" /> : <EyeSlash size={12} />}
                          {item.active ? 'Live' : 'Hidden'}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEdit(item)} data-testid={`promo-edit-${item[config.idField]}`}
                            className="p-2 hover:bg-[#002FA7]/10 text-[#002FA7] transition-colors duration-300" title="Edit">
                            <PencilSimple size={16} weight="bold" />
                          </button>
                          <button onClick={() => remove(item)} data-testid={`promo-delete-${item[config.idField]}`}
                            className="p-2 hover:bg-red-50 text-red-500 transition-colors duration-300" title="Delete">
                            <Trash size={16} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {editing && (
        <EditorModal
          editing={editing}
          config={CONFIGS[editing.type]}
          onChange={(k, v) => setEditing(e => ({ ...e, item: { ...e.item, [k]: v } }))}
          onSave={save}
          onClose={closeEditor}
        />
      )}
    </div>
  );
};

const EditorModal = ({ editing, config, onChange, onSave, onClose }) => {
  const { mode, item } = editing;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="promo-editor" className="bg-white border border-slate-200 rounded-none shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{mode === 'create' ? 'Create' : 'Edit'}</div>
            <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{config.label.slice(0, -1)}</h2>
          </div>
          <button onClick={onClose} data-testid="promo-editor-close" className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {config.fields.map(f => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              {f.type === 'textarea' ? (
                <textarea
                  data-testid={`promo-field-${f.key}`}
                  value={item[f.key] ?? ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  required={f.required}
                  rows={3}
                  className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none transition-colors duration-300"
                />
              ) : f.type === 'select' ? (
                <select
                  data-testid={`promo-field-${f.key}`}
                  value={item[f.key] ?? ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  className="w-full rounded-none border border-slate-300 p-3 outline-none"
                >
                  {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : f.type === 'checkbox' ? (
                <button
                  type="button"
                  data-testid={`promo-field-${f.key}`}
                  onClick={() => onChange(f.key, !item[f.key])}
                  className={`flex items-center gap-2 px-4 py-2 border transition-colors duration-300 ${item[f.key] ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                >
                  {item[f.key] ? <Check size={14} weight="bold" /> : <X size={14} />}
                  {item[f.key] ? 'Live' : 'Hidden'}
                </button>
              ) : f.type === 'color' ? (
                <div className="flex items-center gap-3">
                  <input
                    data-testid={`promo-field-${f.key}`}
                    type="color"
                    value={item[f.key] ?? '#002FA7'}
                    onChange={e => onChange(f.key, e.target.value)}
                    className="w-14 h-11 rounded-none border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={item[f.key] ?? ''}
                    onChange={e => onChange(f.key, e.target.value)}
                    className="flex-1 rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none font-mono"
                  />
                </div>
              ) : (
                <input
                  data-testid={`promo-field-${f.key}`}
                  type={f.type}
                  value={item[f.key] ?? ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  required={f.required}
                  className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none transition-colors duration-300"
                />
              )}
            </div>
          ))}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-none border border-slate-300 hover:bg-slate-50 font-medium transition-colors duration-300">Cancel</button>
          <button onClick={onSave} data-testid="promo-save-btn" className="px-6 py-2.5 rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] font-medium transition-colors duration-300 flex items-center gap-2">
            <Check size={16} weight="bold" /> {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AnalyticsPanel = ({ analytics }) => {
  if (!analytics) return <p className="text-slate-500 text-center py-12">Analytics unavailable</p>;
  const { total_redemptions, unique_customers, by_offer = [], trend_14d = [], recent = [] } = analytics;
  return (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatBox label="Total Redemptions" value={total_redemptions} color="#002FA7" icon={ChartLine} />
        <StatBox label="Unique Customers" value={unique_customers} color="#10B981" icon={Sparkle} />
        <StatBox label="Offers Redeemed" value={by_offer.length} color="#F59E0B" icon={Tag} />
      </div>

      {/* Trend Chart */}
      <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Last 14 Days</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={trend_14d}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="date" tick={CHART_TICK_XS} stroke="#475569" />
            <YAxis tick={CHART_TICK_XS} stroke="#475569" allowDecimals={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Line type="monotone" dataKey="count" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Per-Offer Chart */}
      <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Redemptions by Offer</h3>
        {by_offer.length === 0 ? (
          <p className="text-slate-500 py-8 text-center">No redemptions yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={by_offer} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis type="number" tick={CHART_TICK_XS} stroke="#475569" allowDecimals={false} />
              <YAxis type="category" dataKey="offer_title" tick={CHART_TICK_XS} stroke="#475569" width={120} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Recent Redemptions */}
      <div className="bg-white border border-slate-200 rounded-none shadow-sm">
        <h3 className="text-lg font-semibold px-6 py-4 border-b border-slate-200" style={{fontFamily: 'Outfit, sans-serif'}}>Recent Redemptions</h3>
        {recent.length === 0 ? (
          <p className="text-slate-500 py-8 text-center">No redemptions yet</p>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Offer</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Code</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Customer</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.redemption_id} className="border-b border-slate-100">
                  <td className="px-5 py-3 text-sm font-medium">{r.offer_title}</td>
                  <td className="px-5 py-3 text-sm font-mono">{r.code}</td>
                  <td className="px-5 py-3 text-sm text-slate-600 font-mono text-xs">{r.user_id}</td>
                  <td className="px-5 py-3 text-sm text-slate-500">{new Date(r.redeemed_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, color, icon: IIcon }) => (
  <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
    <IIcon size={24} weight="duotone" style={{color}} className="mb-3" />
    <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color}}>{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
  </div>
);

export default PromotionsManager;
